import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import usePhotoActions from '../hooks/usePhotoActions';
import { getSuggestions, generateStableKey, formatRelativeTime, getItemSortKey } from '../utils/helpers';
import { getAuthenticatedStorageUrl } from '../utils/image';
import { del as apiDelete } from '../utils/apiClient';
import { CameraIcon } from '../assets/icons';
import SwipeableItem from './SwipeableItem';
import PhotoCapture from './PhotoCapture';
import CustomPromptModal from './CustomPromptModal';
import AuthenticatedImage from './AuthenticatedImage';
import PropertyTag from './PropertyTag';

const ListDetailView = ({ user, list, items, onBack, onUpdateItem, onDeleteItem, onClearCompleted, onCreateItem, onAiSort, history, onDeleteFromHistory, onAddProperties, onTranslateItems, onExpandSubitems, onCollapseSubitems, onToggleSubitem, onUpdateListVisibility, activeTranslation, showShareModal, setShowShareModal, handleCopyLink, linkCopied, getShareLink, onUpdateCustomPrompts, onProcessItemsWithPrompts, setCache, showCustomPromptModal, setShowCustomPromptModal, canUserShare, onRenameItem, highlightedItems, isOnline }) => {

  const [newItemContent, setNewItemContent] = useState('');


  const [aiProvider, setAiProvider] = useState('gemini-2.5-flash-lite');
  const [sortedItems, setSortedItems] = useState(items);
  const [currentSortType, setCurrentSortType] = useState('newest');
  // Items that just appeared in this view get pinned to the top until the user
  // explicitly re-sorts (clicks a property pill or picks a sort). This stops
  // newly-added items from drifting between groups as their AI properties
  // arrive a moment after creation.
  const [freshItemIds, setFreshItemIds] = useState(new Set());
  const knownItemIdsRef = useRef(null);
  const [completedLast, setCompletedLast] = useState(() => {
    const saved = localStorage.getItem(`kanto-completed-last-${list.id}`);
    return saved === 'true';
  });
  const [autoSortByAisle, setAutoSortByAisle] = useState(() => {
    const saved = localStorage.getItem(`kanto-auto-sort-${list.id}`);
    return saved === 'true';
  });
  const [isPublic, setIsPublic] = useState(list.visibility === 'public');
  const [isEditable, setIsEditable] = useState(() => {
    // Default logic: public lists are NOT editable, account-required lists ARE editable
    if (list.visibility === 'public') {
      return list.public_editable || false;
    } else {
      return true; // Account required lists default to editable
    }
  });
  const [customPrompts, setCustomPrompts] = useState(list.custom_prompts || []);
  const [isProcessingItems, setIsProcessingItems] = useState(false);
  const [isPhotoProcessing, setIsPhotoProcessing] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showItemImageModal, setShowItemImageModal] = useState(false);
  const [selectedItemForImage, setSelectedItemForImage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const usePhotoActionsRef = useRef({ analyzePhoto: async () => {}, attachItemImage: async () => {} });

  // Inline editing state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [showEditSuggestions, setShowEditSuggestions] = useState(false);
  const [editSelectedIndex, setEditSelectedIndex] = useState(-1);

  const [dropdownPosition, setDropdownPosition] = useState('bottom');

  const startEditingItem = (item) => {
    setEditingItemId(item.id);
    setEditingValue(item.content || '');
    // Initialize suggestions
    const initial = getSuggestions(history, item.content || '', items);
    setEditSuggestions(initial);
    setShowEditSuggestions(initial.length > 0);
    setEditSelectedIndex(-1);

    // Check if this is the first item - if so, show dropdown below instead of above
    const itemIndex = sortedItems.findIndex(i => i.id === item.id);
    setDropdownPosition(itemIndex === 0 ? 'below' : 'above');
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setEditingValue('');
    setShowEditSuggestions(false);
    setEditSuggestions([]);
    setEditSelectedIndex(-1);
  };

  const saveEditing = async (item, overrideValue = null) => {
    const newValue = ((overrideValue ?? editingValue) || '').trim();
    if (!newValue) {
      cancelEditing();
      return;
    }

    if (newValue === (item.content || '')) {
      cancelEditing();
      return;
    }

    try {
      if (onRenameItem) {
        await onRenameItem(list.id, item, newValue);
      } else {
        // Fallback: basic content update
        await onUpdateItem(list.id, item.id, { content: newValue });
      }
    } finally {
      cancelEditing();
    }
  };

  // Update suggestions while editing
  useEffect(() => {
    if (!editingItemId) return;
    if (!editingValue || editingValue.length === 0) {
      setShowEditSuggestions(false);
      setEditSuggestions([]);
      setEditSelectedIndex(-1);
      return;
    }
    const newSugs = getSuggestions(history, editingValue, items);
    setEditSuggestions(newSugs);
    setShowEditSuggestions(newSugs.length > 0);
    setEditSelectedIndex(-1);
  }, [editingValue, history, editingItemId]);

  // Sync the hook functions into a ref so event handlers can call latest logic
  const photoActions = usePhotoActions(list.id, setCache, { onClosePhotoModal: () => setShowPhotoModal(false) });
  useEffect(() => {
    usePhotoActionsRef.current = photoActions;
  }, [photoActions]);

  // Apply current sort when items change
  React.useEffect(() => {
    // Always apply sorting to ensure completed last is applied
    applySortToItems(items, currentSortType);
  }, [items, currentSortType, completedLast]);



  const applySortToItems = (itemsToSort, sortType) => {
    if (sortType.startsWith('ai-')) {
      // For AI sorts, just use the items as-is since they're already sorted by the server
      setSortedItems(itemsToSort);
      return;
    }

    // Filter out undefined items and ensure all items have stable keys
    const sorted = [...itemsToSort]
      .filter(item => item && item.content) // Filter out undefined/null items
      .map(item => ({
        ...item,
        stableKey: item.stableKey || generateStableKey(item)
      }));

    switch (sortType) {
      case 'name-asc':
        sorted.sort((a, b) => a.content.localeCompare(b.content));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.content.localeCompare(a.content));
        break;
      case 'newest':
        sorted.sort((a, b) => getItemSortKey(b) - getItemSortKey(a)); // Newest first
        break;
      case 'oldest':
        sorted.sort((a, b) => getItemSortKey(a) - getItemSortKey(b));
        break;
      default:
        // Handle property-based sorting
        if (sortType.startsWith('property-')) {
          const propertyKey = sortType.replace('property-', '');
          sorted.sort((a, b) => {
            const aValue = a.properties?.[propertyKey];
            const bValue = b.properties?.[propertyKey];

            // Handle null/undefined values
            if (!aValue && !bValue) return 0;
            if (!aValue) return 1;
            if (!bValue) return -1;

            // Always sort alphabetically for string values
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              return aValue.localeCompare(bValue);
            }

            // For numbers, sort numerically
            if (typeof aValue === 'number' && typeof bValue === 'number') {
              return aValue - bValue;
            }

            // Convert to string for comparison (fallback)
            return aValue.toString().localeCompare(bValue.toString());
          });
        }
        break;
    }

    // Apply completed last sorting if enabled
    if (completedLast) {
      sorted.sort((a, b) => (a.is_completed ? 1 : 0) - (b.is_completed ? 1 : 0));
    }

    setSortedItems(sorted);
  };

  // Completion candidate: the first past entry that starts with the user's
  // current input (case-insensitive), excluding things they've already typed in
  // full. Candidates come from BOTH the items already in this list and the
  // deletion history — so previously-added items autofill too, not just deleted
  // ones.
  //
  // The suggestion is *offered*, never silently applied: it renders as a visible
  // row under the input, Tab fills it into the field, and plain Enter adds
  // exactly what was typed. Only an actively selected suggestion (arrow keys or
  // a tap on the row) is submitted in place of the typed text. That default is
  // what makes this safe on touch, where none of the keyboard affordances
  // exist: ignoring the suggestion is simply submitting normally.
  const addSuggestion = React.useMemo(() => {
    const typed = newItemContent;
    if (!typed) return '';
    const typedLower = typed.toLowerCase();
    const candidates = [
      ...(items || []).map(i => i && i.content),
      ...(history || []),
    ];
    const seen = new Set();
    for (const c of candidates) {
      if (typeof c !== 'string') continue;
      const key = c.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      if (c.length > typed.length && key.startsWith(typedLower)) return c;
    }
    return '';
  }, [newItemContent, history, items]);

  // Suggestion row visibility + whether it is the active (selected) choice.
  // Dismissal is keyed by the suggestion *text* rather than a flag, so a
  // dismissed suggestion stays hidden while it is still the same one — typing
  // another character doesn't resurrect it — while a genuinely different
  // suggestion is still offered.
  const [dismissedSuggestion, setDismissedSuggestion] = useState('');
  const [suggestionActive, setSuggestionActive] = useState(false);
  const showAddSuggestion = !!addSuggestion && addSuggestion !== dismissedSuggestion;

  // Selection can't outlive the suggestion that produced it.
  useEffect(() => {
    if (!showAddSuggestion && suggestionActive) setSuggestionActive(false);
  }, [showAddSuggestion, suggestionActive]);

  // Escape on a keyboard, the × button on touch — same effect.
  const dismissSuggestion = () => {
    setDismissedSuggestion(addSuggestion);
    setSuggestionActive(false);
  };

  const resetSuggestionState = () => {
    setDismissedSuggestion('');
    setSuggestionActive(false);
  };

  const addItemValue = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    onCreateItem(list.id, trimmed);
    setNewItemContent('');
    resetSuggestionState();
  };

  const handleCreate = e => {
    e.preventDefault();
    // Enter submits the typed text unless the suggestion row is actively selected.
    addItemValue(suggestionActive && showAddSuggestion ? addSuggestion : newItemContent);
  };

  const handleSuggestionSelect = (suggestion) => {
    // Add the suggestion directly and clear the input
    onCreateItem(list.id, suggestion);
    setNewItemContent('');
  };

  const handleDeleteFromHistory = (suggestion) => {
    onDeleteFromHistory(suggestion);
  };

  const completedCount = (items || []).filter(i => i.is_completed).length;

  const handleClearDone = () => {
    if (completedCount === 0 || !onClearCompleted) return;
    const label = completedCount === 1 ? 'this 1 completed item' : `these ${completedCount} completed items`;
    if (window.confirm(`Remove ${label}?`)) {
      onClearCompleted(list.id);
    }
  };



  const handleUpdateSharing = async () => {
    try {
      const visibility = isPublic ? 'public' : 'private';
      const allow_link_sharing = !isPublic; // If private (requires account), enable link sharing

      await onUpdateListVisibility(list.id, visibility, isEditable, allow_link_sharing);
      setShowShareModal(false);
      alert('Share settings updated successfully!');
    } catch (error) {
      alert('Failed to update share settings: ' + error.message);
    }
  };

  const handlePublicToggle = (newIsPublic) => {
    setIsPublic(newIsPublic);
    // Apply smart defaults when switching modes
    if (newIsPublic) {
      setIsEditable(false); // Public defaults to NOT editable
    } else {
      setIsEditable(true); // Account required defaults to editable
    }
  };



  const handleSort = (sortType, aiProviderParam) => {
    // User explicitly chose a sort — release any pinned-fresh items.
    setFreshItemIds(new Set());
    setCurrentSortType(sortType);

    if (sortType.startsWith('ai-')) {
      // AI sort
      const aiSortType = sortType.replace('ai-', '');
      onAiSort(list.id, aiProviderParam || aiProvider, aiSortType);
    } else if (sortType.startsWith('property-')) {
      // Property-based sort
      const propertyKey = sortType.replace('property-', '');
      handlePropertySort(propertyKey);
    } else {
      // Local sort
      applySortToItems(items, sortType);
    }
  };

  const handlePropertySort = (propertyKey, propertyValue = null) => {
    // User explicitly clicked a property pill — release pinned-fresh items.
    setFreshItemIds(new Set());
    const sortType = `property-${propertyKey}`;
    setCurrentSortType(sortType);

    // Filter out undefined items and ensure all items have stable keys
    const sorted = [...items]
      .filter(item => item && item.content) // Filter out undefined/null items
      .map(item => ({
        ...item,
        stableKey: item.stableKey || generateStableKey(item)
      }));
    sorted.sort((a, b) => {
      const aValue = a.properties?.[propertyKey];
      const bValue = b.properties?.[propertyKey];

      // Handle null/undefined values
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      // Always sort alphabetically for string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue);
      }

      // For numbers, sort numerically
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      // Convert to string for comparison (fallback)
      return aValue.toString().localeCompare(bValue.toString());
    });

    // Apply completed last sorting if enabled
    if (completedLast) {
      sorted.sort((a, b) => (a.is_completed ? 1 : 0) - (b.is_completed ? 1 : 0));
    }

    setSortedItems(sorted);
  };

  const handleSaveCustomPrompts = async (prompts) => {
    setCustomPrompts(prompts);
    if (onUpdateCustomPrompts) {
      await onUpdateCustomPrompts(list.id, prompts);
    }
  };

  const handleProcessItems = async () => {
    if (!onProcessItemsWithPrompts) return;

    // console.log(`🔄 [Process Items] Starting process for list ${list.id}`);
    // console.log(`🔄 [Process Items] Current items count: ${items.length}`);
    // console.log(`🔄 [Process Items] Current custom prompts:`, customPrompts);

    if (items.length === 0) {
      alert('No items found in this list. Please add some items before processing with custom prompts.');
      return;
    }

    if (!customPrompts || customPrompts.length === 0) {
      alert('No custom prompts configured. Please add at least one prompt before processing items.');
      return;
    }

    setIsProcessingItems(true);
    try {
      // Add a small delay to ensure prompts are saved before processing
      await new Promise(resolve => setTimeout(resolve, 500));

      await onProcessItemsWithPrompts(list.id);
      // The items will be updated automatically through the cache
    } catch (error) {
      console.error('Failed to process items:', error);
      const errorMessage = error.response?.data?.message || error.message;
      alert('Failed to process items with custom prompts: ' + errorMessage);
    } finally {
      setIsProcessingItems(false);
    }
  };

  const handlePhotoCapture = () => {
    setShowPhotoModal(true);
  };

  const handleMicrophoneCapture = () => {
    // Check if the browser supports speech recognition
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    // Create speech recognition instance
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    // Configure recognition settings
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    // Start recognition
    setIsRecording(true);
    recognition.start();

    // Handle results
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setNewItemContent(transcript);
      setIsRecording(false);
    };

    // Handle errors
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone permissions and try again.');
      } else {
        alert('Speech recognition error: ' + event.error);
      }
    };

    // Handle when recognition ends
    recognition.onend = () => {
      console.log('Speech recognition ended');
      setIsRecording(false);
    };
  };

  const handlePhotoAnalysis = async (photoData) => {
    setIsPhotoProcessing(true);
    try {
      await usePhotoActionsRef.current.analyzePhoto(photoData);
    } finally {
      setIsPhotoProcessing(false);
    }
  };

  const handleAddImageToItem = (item) => {
    setSelectedItemForImage(item);
    setShowItemImageModal(true);
  };

  const handleImageCaptureForItem = async (photoData) => {
    try {
      await usePhotoActionsRef.current.attachItemImage(selectedItemForImage.id, photoData);
      setShowItemImageModal(false);
      setSelectedItemForImage(null);
    } catch (_e) {}
  };

  // Function to get authenticated download URL for Firebase Storage
  const getAuthenticatedImageUrl = async (imageUrl, item = null) => {
    if (item?.images?.primary?.isOptimistic) return imageUrl;
    return getAuthenticatedStorageUrl(imageUrl);
  };

  const handleViewImage = async (imageUrl, item = null) => {
    // Ensure we're using the best available image URL
    let bestImageUrl = item?.images?.primary?.isOptimistic
      ? imageUrl
      : (item?.images?.primary?.mediumUrl || imageUrl);

    // Get authenticated URL if needed
    const authenticatedUrl = await getAuthenticatedImageUrl(bestImageUrl, item);

    console.log('🖼️ [View Image] Opening image viewer with URL:', authenticatedUrl);
    console.log('🖼️ [View Image] Item:', item?.id, item?.content);
    console.log('🖼️ [View Image] Is optimistic:', item?.images?.primary?.isOptimistic);

    setSelectedImage(authenticatedUrl);
    setSelectedItemForImage(item);
    setShowImageModal(true);
  };

  const handleRemoveImage = async () => {
    if (!selectedItemForImage) return;

    // Optimistic update: remove image from local state immediately
    const itemId = selectedItemForImage.id;
    const listId = list.id;

    setCache(c => ({
      ...c,
      items: {
        ...c.items,
        [listId]: c.items[listId].map(item =>
          item.id === itemId
            ? { ...item, image: null, images: null } // Remove both old and new image structures
            : item
        )
      }
    }));

    // Close modal immediately for better UX
    setShowImageModal(false);
    setSelectedImage(null);
    setSelectedItemForImage(null);

    try {
      const { data: result } = await apiDelete(`/lists/${listId}/items/${itemId}/image`);
      if (!result?.success) {
        setCache((c) => ({
          ...c,
          items: {
            ...c.items,
            [listId]: c.items[listId].map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    image: selectedItemForImage.image,
                    images: selectedItemForImage.images,
                  }
                : item
            ),
          },
        }));
        console.error('Server failed to remove image, reverted local change');
      }
    } catch (error) {
      // Treat 404 as success to keep deletion idempotent
      if (error?.response?.status !== 404) {
        console.error('Failed to remove image:', error);
        setCache((c) => ({
          ...c,
          items: {
            ...c.items,
            [listId]: c.items[listId].map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    image: selectedItemForImage.image,
                    images: selectedItemForImage.images,
                  }
                : item
            ),
          },
        }));
      }
    }
  };

  const handleRetakeImage = () => {
    setShowImageModal(false);
    setShowItemImageModal(true);
  };

  // Track which item IDs are "freshly added in this view" so we can pin them
  // to the top until the user explicitly re-sorts. Items appearing on the very
  // first render don't count as fresh — only items that show up later do.
  React.useEffect(() => {
    const currentIds = new Set(items.filter(i => i && i.id).map(i => i.id));
    if (knownItemIdsRef.current === null) {
      knownItemIdsRef.current = currentIds;
      return;
    }
    setFreshItemIds(prev => {
      let next = new Set(prev);
      // Add anything that just appeared
      currentIds.forEach(id => {
        if (!knownItemIdsRef.current.has(id)) next.add(id);
      });
      // Drop anything that's no longer in the list (e.g., temp ids replaced
      // by real ones, or items deleted)
      next = new Set([...next].filter(id => currentIds.has(id)));
      return next;
    });
    knownItemIdsRef.current = currentIds;
  }, [items]);

  // Group items by department/category for display (only when autoSortByAisle is enabled)
  const groupedItems = React.useMemo(() => {
    // First, handle completed items ordering
    let itemsToGroup = [...sortedItems];
    if (completedLast) {
      itemsToGroup.sort((a, b) => {
        if (a.is_completed === b.is_completed) return 0;
        return a.is_completed ? 1 : -1;
      });
    }

    // Peel off freshly-added items so they stay pinned at the top regardless
    // of grouping/sorting. They stay pinned until the user explicitly re-sorts.
    const fresh = itemsToGroup.filter(i => freshItemIds.has(i.id));
    const rest = itemsToGroup.filter(i => !freshItemIds.has(i.id));

    // If auto-sort by aisle is disabled, put all items in a single group
    if (!autoSortByAisle) {
      return { 'All Items': [...fresh, ...rest] };
    }

    // Group the rest by department/category. Fresh items go in a leading
    // group with no header, so they visually sit above the section headers.
    const groups = {};
    if (fresh.length > 0) {
      groups['__fresh__'] = fresh;
    }
    rest.forEach(item => {
      const department = item.properties?.department || item.properties?.category || 'Other';
      const aisle = item.properties?.aisle ? ` (Aisle ${item.properties.aisle})` : '';
      const groupKey = `${department}${aisle}`;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });
    return groups;
  }, [sortedItems, autoSortByAisle, completedLast, freshItemIds]);

  // Render a single item row
  const renderItem = (item) => {
    const hasSuggestions = Array.isArray(item.suggested_subitems) && item.suggested_subitems.length > 0;
    const isExpanded = Array.isArray(item.subitems) && item.subitems.length > 0;
    const showSubitemsChip = hasSuggestions || isExpanded;

    return (
    <SwipeableItem
      key={item.stableKey || item.id || `temp-${item.content}-${Date.now()}`}
      onDelete={() => onDeleteItem(list.id, item.id)}
      threshold={80}
    >
      <div
        className={`list-item-new ${item.is_completed ? 'completed' : ''} ${highlightedItems?.has(item.id) ? 'item-highlight' : ''}`}
      >
        <div
          className={`list-item-checkbox ${item.is_completed ? 'checked' : ''}`}
          onClick={() => onUpdateItem(list.id, item.id, { is_completed: !item.is_completed })}
        />

        {/* Image Display */}
        {(item.images?.primary || item.image) && (
          <AuthenticatedImage
            item={item}
            onViewImage={handleViewImage}
            onRemoveImage={() => {
              setSelectedItemForImage(item);
              handleRemoveImage();
            }}
          />
        )}

        <div className="list-item-content-new">
          <span className="list-item-name-new" onClick={(e) => { e.stopPropagation(); startEditingItem(item); }}>
            {editingItemId === item.id ? (
              <span className="inline-edit-wrapper" onClick={(e)=> e.stopPropagation()} style={{ position: 'relative', display: 'inline-block', width: '100%', zIndex: 9998 }}>
                <input
                  className="inline-edit-input"
                  value={editingValue}
                  autoFocus
                  onChange={(e) => setEditingValue(e.target.value)}
                  onBlur={() => setTimeout(() => saveEditing(item), 150)}
                  onKeyDown={(e) => {
                    if (!showEditSuggestions) {
                      if (e.key === 'Enter') { e.preventDefault(); saveEditing(item); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); }
                      return;
                    }
                    switch (e.key) {
                      case 'ArrowDown':
                        e.preventDefault();
                        setEditSelectedIndex(prev => Math.min(prev + 1, editSuggestions.length - 1));
                        break;
                      case 'ArrowUp':
                        e.preventDefault();
                        setEditSelectedIndex(prev => Math.max(prev - 1, -1));
                        break;
                      case 'Enter':
                        e.preventDefault();
                        if (editSelectedIndex >= 0) {
                          const choice = editSuggestions[editSelectedIndex];
                          setShowEditSuggestions(false);
                          setTimeout(() => saveEditing(item, choice), 0);
                        } else {
                          saveEditing(item);
                        }
                        break;
                      case 'Escape':
                        cancelEditing();
                        break;
                      default:
                        break;
                    }
                  }}
                />
                {showEditSuggestions && editSuggestions.length > 0 && (
                  <div
                    className={`floating-suggestions-dropdown ${dropdownPosition === 'below' ? 'dropdown-below' : ''}`}
                    style={{
                      position: 'absolute',
                      zIndex: 9999,
                      ...(dropdownPosition === 'below' ? {
                        top: '100%',
                        bottom: 'auto',
                        marginTop: '8px',
                        marginBottom: 0
                      } : {})
                    }}
                  >
                    {editSuggestions.map((s, idx) => (
                      <div
                        key={`${s}-${idx}`}
                        className={`floating-suggestion-item ${idx === editSelectedIndex ? 'selected' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); setShowEditSuggestions(false); setTimeout(() => saveEditing(item, s), 0); }}
                      >
                        <span className="floating-suggestion-text">{s}</span>
                      </div>
                    ))}
                  </div>
                )}
              </span>
            ) : (
              item.content
            )}
          </span>

          {/* Timestamp + property pills (timestamp always first) */}
          <div className="list-item-properties-new">
            {item.created_at && (
              <button
                type="button"
                className={`time-pill ${(currentSortType === 'newest' || currentSortType === 'oldest') ? 'active' : ''}`}
                title={`Sort by time added (${currentSortType === 'newest' ? 'newest first — click for oldest' : 'click to sort newest first'})`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort(currentSortType === 'newest' ? 'oldest' : 'newest');
                }}
              >
                {formatRelativeTime(item.created_at)}
              </button>
            )}
            {item.properties && Object.entries(item.properties)
              .filter(([key, value]) => {
                if (key.startsWith('name_')) return false;
                if (typeof value === 'object' && value !== null) return false;
                return true;
              })
              .map(([key, value]) => (
                <PropertyTag key={key} propertyKey={key} value={value} list={null} onManageAccess={null} onPropertySort={handlePropertySort} customPrompts={list.custom_prompts || []} />
              ))}
          </div>

          {/* Inline sub-items, when expanded */}
          {isExpanded && (
            <div className="subitems-list">
              {item.subitems.map(sub => (
                <div
                  key={sub.id}
                  className={`subitem-row ${sub.is_completed ? 'completed' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onToggleSubitem && onToggleSubitem(list.id, item, sub.id); }}
                >
                  <div className={`list-item-checkbox ${sub.is_completed ? 'checked' : ''}`} />
                  <span className="subitem-name">{sub.content}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {showSubitemsChip && (
          <button
            type="button"
            className={`subitems-chip ${isExpanded ? 'expanded' : ''}`}
            title={isExpanded ? 'Collapse sub-items' : 'Show suggested sub-items'}
            aria-label={isExpanded ? 'Collapse sub-items' : 'Show suggested sub-items'}
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              if (isExpanded) {
                onCollapseSubitems && onCollapseSubitems(list.id, item);
              } else {
                onExpandSubitems && onExpandSubitems(list.id, item);
              }
            }}
          >
            <svg
              className="subitems-chip-chevron"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>
    </SwipeableItem>
    );
  };

  return (
    <div className="list-detail-view">
      <div className="list-view-header">
        <button onClick={onBack} className="list-view-back-btn">
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>{list.name}</h2>
        {completedCount > 0 && (
          <button
            onClick={handleClearDone}
            className="list-view-clear-done-btn"
            title="Remove all completed items"
          >
            Clear done
          </button>
        )}
        <button onClick={() => setShowCustomPromptModal(true)} className="list-view-ai-btn">
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </button>
      </div>

      {/* Offline indicator bar */}
      {isOnline === false && (
        <div className="offline-bar">
          Offline mode - changes will sync when connected
        </div>
      )}

      {/* Add Item Input - at top like mockup */}
      <div className="list-add-item-section">
        <form onSubmit={handleCreate} className="list-add-item-form">
          <div className="list-add-item-input-wrap">
            <input
              type="text"
              value={newItemContent}
              onChange={e => {
                setNewItemContent(e.target.value);
                // Typing drops any selection, so Enter always adds what's in the
                // field unless the row is deliberately re-selected.
                setSuggestionActive(false);
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Tab' || e.key === 'ArrowRight') && showAddSuggestion) {
                  // Tab / Right Arrow: fill the suggestion into the input without
                  // submitting. (Right Arrow only when the caret is at the end —
                  // otherwise default arrow navigation.)
                  if (e.key === 'ArrowRight' && e.target.selectionStart !== newItemContent.length) return;
                  e.preventDefault();
                  setNewItemContent(addSuggestion);
                  setSuggestionActive(false);
                  return;
                }
                if (e.key === 'ArrowDown' && showAddSuggestion) {
                  e.preventDefault();
                  setSuggestionActive(true);
                  return;
                }
                if (e.key === 'ArrowUp' && suggestionActive) {
                  e.preventDefault();
                  setSuggestionActive(false);
                  return;
                }
                if (e.key === 'Escape' && showAddSuggestion) {
                  e.preventDefault();
                  dismissSuggestion();
                }
              }}
              placeholder="Add item or 'Taco Night'..."
              className="list-add-item-input"
              disabled={list.id.startsWith('temp-')}
              autoComplete="off"
              spellCheck="false"
              role="combobox"
              aria-expanded={showAddSuggestion}
              aria-controls="list-add-item-suggestion"
              aria-activedescendant={suggestionActive ? 'list-add-item-suggestion' : undefined}
            />
          </div>
          <button
            type="button"
            onClick={handleMicrophoneCapture}
            className="list-mic-button"
            disabled={list.id.startsWith('temp-')}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
        </form>

        {/* Explicit, selectable completion. Replaces the old ghost overlay, which
            looked like decoration but silently changed what Enter submitted.
            Every affordance here has a touch equivalent: tap the row to accept,
            tap × to dismiss. The keyboard hint is hidden on coarse pointers,
            where it would name keys the device doesn't have. */}
        {showAddSuggestion && (
          <div className="list-add-item-suggestions">
            <div className="list-add-item-suggestion-listbox" role="listbox" aria-label="Suggested item">
              <button
                type="button"
                id="list-add-item-suggestion"
                role="option"
                aria-selected={suggestionActive}
                className={`list-add-item-suggestion ${suggestionActive ? 'is-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addItemValue(addSuggestion)}
              >
                <span className="list-add-item-suggestion-label">Add</span>
                <span className="list-add-item-suggestion-text">
                  <span className="suggestion-typed">{addSuggestion.slice(0, newItemContent.length)}</span>
                  <span className="suggestion-remainder">{addSuggestion.slice(newItemContent.length)}</span>
                </span>
                <span className="list-add-item-suggestion-hint" aria-hidden="true">
                  {suggestionActive ? '↵' : 'Tab'}
                </span>
              </button>
            </div>
            <button
              type="button"
              className="list-add-item-suggestion-dismiss"
              aria-label={`Dismiss suggestion ${addSuggestion}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={dismissSuggestion}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        )}
      </div>

      <div className="list-view-content">
        <div className="items-container">
          <AnimatePresence initial={false}>
            {Object.entries(groupedItems).map(([groupName, groupItems]) => (
              <div key={groupName} className="item-category-group">
                {/* Suppress the header for "All Items" and the pinned-fresh bucket. */}
                {autoSortByAisle && groupName !== 'All Items' && groupName !== '__fresh__' && (
                  <div className="item-category-header">{groupName.toUpperCase()}</div>
                )}
                {groupItems.map(item => renderItem(item))}
              </div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share List</h3>
              <button onClick={() => setShowShareModal(false)} className="modal-close">×</button>
            </div>
            <div className="share-form">
              {/* Share Link Section */}
              <div className="form-group">
                <label>Share Link:</label>
                <div className="share-link-container">
                  <input
                    type="text"
                    value={getShareLink(list.id)}
                    readOnly
                    className="share-link-input"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopyLink(list.id)}
                    className={`copy-link-btn ${linkCopied ? 'copied' : ''}`}
                  >
                    {linkCopied ? '✓' : '📋'}
                  </button>
                </div>
                <p className="share-link-note">
                  {!isPublic ? 'Requires Account' : 'Public'} • {isEditable ? 'Editable' : 'View Only'}
                </p>
              </div>

              {/* Share Settings */}
              <div className="form-group">
                <label>Share Settings:</label>
                <div className="share-settings">
                  <div
                    className={`share-block ${!isPublic ? 'selected' : ''}`}
                    onClick={() => handlePublicToggle(!isPublic)}
                  >
                    <div className="share-block-content">
                      <span className="share-block-icon">🔒</span>
                      <span className="share-block-title">Requires Account</span>
                      <span className="share-block-subtitle">Only logged-in users can access</span>
                    </div>
                  </div>

                  <div
                    className={`share-block ${isEditable ? 'selected' : ''}`}
                    onClick={() => setIsEditable(!isEditable)}
                  >
                    <div className="share-block-content">
                      <span className="share-block-icon">✏️</span>
                      <span className="share-block-title">Editable</span>
                      <span className="share-block-subtitle">Users can add and edit items</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowShareModal(false)} className="secondary-button">
                  Cancel
                </button>
                <button type="button" onClick={handleUpdateSharing} className="primary-button">
                  Update Share Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Prompt Modal */}
      <CustomPromptModal
        isOpen={showCustomPromptModal}
        onClose={() => setShowCustomPromptModal(false)}
        customPrompts={customPrompts}
        onSavePrompts={handleSaveCustomPrompts}
        onProcessItems={handleProcessItems}
        isProcessing={isProcessingItems}
        itemsCount={items.length}
        aiProvider={aiProvider}
        onAiProviderChange={setAiProvider}
        autoSortByAisle={autoSortByAisle}
        onAutoSortToggle={(value) => {
          setAutoSortByAisle(value);
          localStorage.setItem(`kanto-auto-sort-${list.id}`, value.toString());
        }}
        completedLast={completedLast}
        onCompletedLastToggle={(value) => {
          setCompletedLast(value);
          localStorage.setItem(`kanto-completed-last-${list.id}`, value.toString());
        }}
        extraContent={(
          canUserShare && canUserShare() ? (
            <div className="share-form" style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 8 }}>Share</h3>
              {/* Share Link Section */}
              <div className="form-group">
                <label>Share Link:</label>
                <div className="share-link-container">
                  <input
                    type="text"
                    value={getShareLink(list.id)}
                    readOnly
                    className="share-link-input"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopyLink(list.id)}
                    className={`copy-link-btn ${linkCopied ? 'copied' : ''}`}
                  >
                    {linkCopied ? '✓' : '📋'}
                  </button>
                </div>
                <p className="share-link-note">
                  {!isPublic ? 'Requires Account' : 'Public'} • {isEditable ? 'Editable' : 'View Only'}
                </p>
              </div>

              {/* Share Settings */}
              <div className="form-group">
                <label>Share Settings:</label>
                <div className="share-settings">
                  <div
                    className={`share-block ${!isPublic ? 'selected' : ''}`}
                    onClick={() => handlePublicToggle(!isPublic)}
                  >
                    <div className="share-block-content">
                      <span className="share-block-icon">🔒</span>
                      <span className="share-block-title">Requires Account</span>
                      <span className="share-block-subtitle">Only logged-in users can access</span>
                    </div>
                  </div>
                  <div
                    className={`share-block ${isEditable ? 'selected' : ''}`}
                    onClick={() => setIsEditable(!isEditable)}
                  >
                    <div className="share-block-content">
                      <span className="share-block-icon">✏️</span>
                      <span className="share-block-title">Editable</span>
                      <span className="share-block-subtitle">Users can add and edit items</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleUpdateSharing} className="primary-button">
                  Update Share Settings
                </button>
              </div>
            </div>
          ) : null
        )}
      />

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="modal-overlay" onClick={() => setShowPhotoModal(false)}>
          <div className="modal-content photo-capture-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Take a Photo</h3>
              <button onClick={() => setShowPhotoModal(false)} className="modal-close">×</button>
            </div>
            <div className="photo-capture-content">
              <PhotoCapture
                onPhotoTaken={handlePhotoAnalysis}
                isProcessing={isPhotoProcessing}
                onClose={() => setShowPhotoModal(false)}
                mode="analyze"
              />
            </div>
          </div>
        </div>
      )}

      {/* Item Image Modal */}
      {showItemImageModal && (
        <div className="modal-overlay" onClick={() => setShowItemImageModal(false)}>
          <div className="modal-content photo-capture-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Image to Item</h3>
              <button onClick={() => setShowItemImageModal(false)} className="modal-close">×</button>
            </div>
            <div className="photo-capture-content">
              <PhotoCapture
                onPhotoTaken={handleImageCaptureForItem}
                isProcessing={false}
                onClose={() => {
                  setShowItemImageModal(false);
                  setSelectedItemForImage(null);
                }}
                mode="attach"
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal-content image-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Image</h3>
              <button onClick={() => setShowImageModal(false)} className="modal-close">×</button>
            </div>
            <div className="image-viewer-content">
              <img
                src={selectedImage}
                alt="Full size"
                className="full-size-image"
                onError={(e) => {
                  console.error('🖼️ [Image Viewer Error] Failed to load full size image:', selectedImage);
                  console.error('🖼️ [Image Viewer Error] Item data:', selectedItemForImage);

                  // Try fallback URLs in order of preference
                  const fallbackUrls = [
                    selectedItemForImage?.images?.primary?.mediumUrl,
                    selectedItemForImage?.image
                  ].filter(Boolean);

                  if (fallbackUrls.length > 0) {
                    console.log('🖼️ [Image Viewer Error] Trying fallback URL:', fallbackUrls[0]);
                    e.target.src = fallbackUrls[0];
                  } else {
                    console.error('🖼️ [Image Viewer Error] No fallback URLs available');
                    // Show error message to user
                    e.target.style.display = 'none';
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'image-viewer-error';
                    errorDiv.innerHTML = '❌ Failed to load image';
                    errorDiv.style.cssText = `
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      height: 200px;
                      color: #f44336;
                      font-size: 16px;
                    `;
                    e.target.parentNode.appendChild(errorDiv);
                  }
                }}
                onLoad={() => {
                  console.log('🖼️ [Image Viewer Success] Loaded full-size image:', selectedImage);
                }}
              />
            </div>
            {selectedItemForImage && (
              <div className="image-viewer-actions">
                <button
                  onClick={handleRemoveImage}
                  className="remove-image-button"
                  title="Remove image"
                >
                  🗑️ Remove Image
                </button>
                <button
                  onClick={handleRetakeImage}
                  className="retake-image-button"
                  title="Retake image"
                >
                  <img src={CameraIcon} alt="Camera" style={{ width: '20px', height: '20px', marginRight: '8px' }} />
                  Retake Image
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListDetailView;
